import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Divider } from 'antd';
import { code as getCurrency, codes as getCurrencyCodes } from 'currency-codes';
import { CloudRain, Flower2, Ghost, Heart, PartyPopper, Snowflake, Sprout, Umbrella, Waves, Wheat } from 'lucide-react';
import {
    TbCalendarDollar,
    TbCalendarEvent,
    TbCalendarWeek,
    TbCurrencyDollar,
    TbLeaf,
    TbSun,
    TbWorldPin,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DropdownButtonOption,
    DropdownButtonSelect,
    DropdownMenuLabel,
    FinancialValueDisplayControl,
    LearnMoreFooter,
    PreferenceRow,
    TimezoneDropdown,
} from '@/shared';
import type { FinancialValueDisplayPreference, Workspace, WorkspaceDateFormat, WorkspaceWeekStartDay } from '@/types';

import { isSelectableReportingCurrency } from './reportingCurrencies';

export type WorkspaceOperationalPreferenceUpdate =
    | { key: 'dateFormat'; value: WorkspaceDateFormat }
    | { key: 'financialValueDisplay'; value: FinancialValueDisplayPreference }
    | { key: 'fiscalYearStartMonth'; value: number }
    | { key: 'reportingCurrency'; value: string }
    | { key: 'weekStartDay'; value: WorkspaceWeekStartDay }
    | { key: 'workspaceTimeZone'; value: string };

export type WorkspaceOperationalPreferenceKey = WorkspaceOperationalPreferenceUpdate['key'];

interface WorkspaceOperationalPreferencesProps {
    additionalPreference?: ReactNode;
    disabled?: boolean;
    onChange: (update: WorkspaceOperationalPreferenceUpdate) => Promise<void>;
    savingPreference?: WorkspaceOperationalPreferenceKey;
    workspace: Workspace;
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
] as const;

const MONTH_ICON_SIZE = '0.875rem';

const MONTH_ICONS: readonly ReactNode[] = [
    <PartyPopper key='january' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Heart key='february' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Sprout key='march' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <CloudRain key='april' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Flower2 key='may' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <TbSun key='june' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Umbrella key='july' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Waves key='august' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <TbLeaf key='september' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Ghost key='october' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Wheat key='november' aria-hidden='true' size={MONTH_ICON_SIZE} />,
    <Snowflake key='december' aria-hidden='true' size={MONTH_ICON_SIZE} />,
];

const DATE_FORMAT_OPTIONS: Array<DropdownButtonOption<WorkspaceDateFormat>> = [
    { description: 'Example: 08/30/2026', label: 'Month / Day / Year', value: 'MM/DD/YYYY' },
    { description: 'Example: 30/08/2026', label: 'Day / Month / Year', value: 'DD/MM/YYYY' },
    { description: 'ISO 8601 example: 2026-08-30', label: 'Year / Month / Day', value: 'YYYY-MM-DD' },
];

const WEEK_START_OPTIONS: Array<DropdownButtonOption<WorkspaceWeekStartDay>> = [
    { description: 'Use Monday through Sunday for weekly planning.', label: 'Monday', value: 'monday' },
    { description: 'Use Tuesday through Monday for weekly planning.', label: 'Tuesday', value: 'tuesday' },
    { description: 'Use Wednesday through Tuesday for weekly planning.', label: 'Wednesday', value: 'wednesday' },
    { description: 'Use Thursday through Wednesday for weekly planning.', label: 'Thursday', value: 'thursday' },
    { description: 'Use Friday through Thursday for weekly planning.', label: 'Friday', value: 'friday' },
    { description: 'Use Saturday through Friday for weekly planning.', label: 'Saturday', value: 'saturday' },
    { description: 'Use Sunday through Saturday for weekly planning.', label: 'Sunday', value: 'sunday' },
];

const WorkspaceOperationalPreferences: FC<WorkspaceOperationalPreferencesProps> = ({
    additionalPreference,
    disabled = false,
    onChange,
    savingPreference,
    workspace,
}) => {
    const { token } = useAntDesignTheme();
    const controlsDisabled = disabled || Boolean(savingPreference);
    const monthOptions = useMemo<Array<DropdownButtonOption<number>>>(
        () =>
            MONTHS.map((month, index) => ({
                description: `Fiscal quarters and annual reporting begin in ${month}.`,
                icon: MONTH_ICONS[index],
                label: month,
                searchText: `${month} month ${index + 1}`,
                value: index + 1,
            })),
        [],
    );
    const currencyOptions = useMemo<Array<DropdownButtonOption<string>>>(
        () =>
            getCurrencyCodes()
                .filter(isSelectableReportingCurrency)
                .flatMap((currencyCode) => {
                    const currency = getCurrency(currencyCode);

                    return currency
                        ? [
                              {
                                  description: currency.currency,
                                  icon: <TbCurrencyDollar aria-hidden='true' />,
                                  label: currencyCode,
                                  searchText: `${currencyCode} ${currency.currency} ${currency.countries.join(' ')}`,
                                  value: currencyCode,
                              },
                          ]
                        : [];
                }),
        [],
    );
    const dropdownProps = {
        disabled: controlsDisabled,
        matchTriggerWidth: false,
        menuMinWidth: '20rem',
        placement: 'bottomRight' as const,
        searchable: true,
    };

    return (
        <section aria-labelledby='workspace-time-reporting-heading'>
            <div
                style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    overflow: 'hidden',
                }}
            >
                <PreferenceRow
                    control={
                        <DropdownButtonSelect<number>
                            {...dropdownProps}
                            aria-label='First month of the fiscal year'
                            footer={<LearnMoreFooter topic='fiscal-year reporting' variant='dropdown' />}
                            header={
                                <DropdownMenuLabel icon={<TbCalendarDollar size='0.875rem' />}>
                                    First Month of the Fiscal Year
                                </DropdownMenuLabel>
                            }
                            loading={savingPreference === 'fiscalYearStartMonth'}
                            options={monthOptions}
                            searchPlaceholder='Search Fiscal Year Months...'
                            value={workspace.fiscalYearStartMonth ?? 1}
                            onChange={(value) => {
                                if (value !== undefined) {
                                    void onChange({ key: 'fiscalYearStartMonth', value });
                                }
                            }}
                        />
                    }
                    description='Choose the month that begins your fiscal year. This determines how research and reports are grouped into fiscal quarters, half-years, and years.'
                    icon={<TbCalendarDollar size='1rem' />}
                    label='First Month of the Fiscal Year'
                />
                <PreferenceRow
                    control={
                        <div style={{ width: '100%' }}>
                            <TimezoneDropdown
                                disabled={controlsDisabled}
                                placement='bottomRight'
                                value={workspace.workspaceTimeZone ?? 'UTC'}
                                onChange={(value) => onChange({ key: 'workspaceTimeZone', value })}
                            />
                        </div>
                    }
                    description='Choose the default timezone for scheduled activity and for dates and times shown in workspace reports and exports.'
                    divided
                    icon={<TbWorldPin size='1rem' />}
                    label='Workspace Timezone'
                />
                <PreferenceRow
                    control={
                        <DropdownButtonSelect<string>
                            {...dropdownProps}
                            aria-label='Default reporting currency'
                            footer={<LearnMoreFooter topic='workspace reporting currencies' variant='dropdown' />}
                            header={
                                <DropdownMenuLabel icon={<TbCurrencyDollar size='0.875rem' />}>
                                    Reporting Currency
                                </DropdownMenuLabel>
                            }
                            loading={savingPreference === 'reportingCurrency'}
                            options={currencyOptions}
                            searchPlaceholder='Search Reporting Currencies...'
                            value={workspace.reportingCurrency ?? 'USD'}
                            onChange={(value) => {
                                if (value) {
                                    void onChange({ key: 'reportingCurrency', value });
                                }
                            }}
                        />
                    }
                    description='Choose the currency shown by default in workspace summaries and reports. This setting does not convert underlying values.'
                    divided
                    icon={<TbCurrencyDollar size='1rem' />}
                    label='Default Reporting Currency'
                />
                <PreferenceRow
                    control={
                        <FinancialValueDisplayControl
                            disabled={controlsDisabled}
                            loading={savingPreference === 'financialValueDisplay'}
                            value={workspace.financialValueDisplay}
                            onChange={(value) => {
                                if (value) {
                                    void onChange({ key: 'financialValueDisplay', value });
                                }
                            }}
                        />
                    }
                    description='Set the default magnitude, precision, and negative-number style for financial research. Members can override this default without changing source values.'
                    divided
                    icon={<TbCurrencyDollar size='1rem' />}
                    label='Financial Value Display'
                />
                <PreferenceRow
                    control={
                        <DropdownButtonSelect<WorkspaceDateFormat>
                            {...dropdownProps}
                            aria-label='Workspace date format'
                            footer={<LearnMoreFooter topic='workspace date formats' variant='dropdown' />}
                            header={
                                <DropdownMenuLabel icon={<TbCalendarEvent size='0.875rem' />}>
                                    Date Format
                                </DropdownMenuLabel>
                            }
                            loading={savingPreference === 'dateFormat'}
                            options={DATE_FORMAT_OPTIONS}
                            searchPlaceholder='Search Date Formats...'
                            value={workspace.dateFormat ?? 'MM/DD/YYYY'}
                            onChange={(value) => {
                                if (value) {
                                    void onChange({ key: 'dateFormat', value });
                                }
                            }}
                        />
                    }
                    description='Choose how dates are displayed by default in shared workspace reports and exported files.'
                    divided
                    icon={<TbCalendarEvent size='1rem' />}
                    label='Date Format'
                />
                <PreferenceRow
                    control={
                        <DropdownButtonSelect<WorkspaceWeekStartDay>
                            {...dropdownProps}
                            aria-label='First day of the workspace week'
                            footer={<LearnMoreFooter topic='workspace calendar conventions' variant='dropdown' />}
                            header={
                                <DropdownMenuLabel icon={<TbCalendarWeek size='0.875rem' />}>
                                    First Day of the Week
                                </DropdownMenuLabel>
                            }
                            loading={savingPreference === 'weekStartDay'}
                            options={WEEK_START_OPTIONS}
                            searchPlaceholder='Search Week Start Days...'
                            value={workspace.weekStartDay ?? 'monday'}
                            onChange={(value) => {
                                if (value) {
                                    void onChange({ key: 'weekStartDay', value });
                                }
                            }}
                        />
                    }
                    description='Choose which day begins each week for research reviews, task planning, and recurring workspace schedules.'
                    divided
                    icon={<TbCalendarWeek size='1rem' />}
                    label='First Day of the Week'
                />
                {additionalPreference ? (
                    <>
                        <Divider style={{ margin: 0 }} />
                        <div style={{ padding: '1rem' }}>{additionalPreference}</div>
                    </>
                ) : null}
            </div>
        </section>
    );
};

export default WorkspaceOperationalPreferences;
